import { get, sum, sumBy, toNumber } from 'lodash';
import {
  FinalFee,
  FinalFeeType,
  FinalProduct,
  FinanceResponseData,
  Insurance,
  InsuranceType,
  OtherCharge,
  UpstartLendingStates,
} from './legos/financingTypes';
import { RICSchema, SumValueType } from './legos/ricForm/ricFormTypes';
import { getTradeInAllowance, getTradeInPayoff } from './dealer-utils';
import { fmtPrice } from './formatter';
import StateRICSchemaMap from './legos/ricForm/stateRICSchemaMap';

/**
 * Returns the RIC schema given a state string abbreviation.
 * Returns undefined if schema doesn't exist
 * @param state - state string abbreviation
 */

export function getRICSchemaForState(state?: string): RICSchema | undefined {
  if (!state) {
    return undefined;
  }

  const upstartState = state as UpstartLendingStates;

  // Return undefined if state not enabled for upstart lending
  if (!Object.values(UpstartLendingStates).includes(upstartState)) {
    return undefined;
  }

  // Return undefined if state does not exist in schema mapping
  if (!StateRICSchemaMap[upstartState]) {
    return undefined;
  }

  return StateRICSchemaMap[state as UpstartLendingStates];
}

/**
 * Get fee by type (single).
 * @param finalizedFeeList - FinalFee Array
 * @param type - FinalFeeType
 */
export function getFinalizedFee(
  finalizedFeeList: FinalFee[] | undefined,
  type: FinalFeeType | undefined,
): FinalFee | undefined {
  return finalizedFeeList?.find(f => f.type === type);
}

/**
 * Get fees by type (multiple).
 * @param finalizedFeeList - FinalFee Array
 * @param type - FinalFeeType
 */
export function getFinalizedFees(
  finalizedFeeList: FinalFee[] | undefined,
  type: FinalFeeType | undefined,
): FinalFee[] {
  return finalizedFeeList?.filter(f => f.type === type) ?? [];
}

/**
 * Get insurance by type (single)
 * @param insurances - Insurance Array
 * @param type - InsuranceType
 */
export function getInsurance(
  insurances: Insurance[] | undefined,
  type: InsuranceType | undefined,
): Insurance | undefined {
  return insurances?.find(i => i.type === type);
}

/**
 * Calculate the sum of all values of products.
 * @param products
 */
export function getProductsTotal(products: FinalProduct[]): number {
  return sumBy(products, 'value') || 0;
}

/**
 * Calculate the sum of all values of fees.
 * @param fees
 */
export function getFeesTotal(fees: FinalFee[]): number {
  return sumBy(fees, 'value') || 0;
}

/**
 * Calculate the sum of all values of insurances.
 * @param insurances
 */
export function getInsurancesTotal(insurances: Insurance[]): number {
  return sumBy(insurances, 'premium') || 0;
}

/**
 * Calculate the sum of all values of other charges.
 * @param otherCharges
 */
export function getOtherChargesTotal(otherCharges: OtherCharge[]): number {
  return sumBy(otherCharges, 'amount') || 0;
}

/**
 * Parses number if present
 * @param val - string val
 */
export function parseNumberIfPresent(val: string): number | null {
  return val === '' ? null : toNumber(val);
}

/**
 * Formats  number to currency string
 * @param number
 */
export function formatCurrency(number?: number | string | null): string {
  if (!number && number !== 0) {
    return 'N/A';
  }
  return toNumber(number) < 0 ? `-${fmtPrice(-number)}` : fmtPrice(number);
}

/**
 * Calculates the total down payment using tradeins, rebate, and other relevant fields
 * @param responseData - responseData
 */

export function calculateTotalDownPayment(
  responseData: FinanceResponseData,
): number {
  const tradeIns = responseData?.tradeIns ?? [];
  const tradeInAllowance = getTradeInAllowance(tradeIns);
  const tradePayoff = getTradeInPayoff(tradeIns);
  const netTrade = tradeInAllowance - tradePayoff;
  return (
    sum([
      responseData?.deferredDownPayment ?? 0,
      sumBy(responseData?.otherDownPayments, 'amount') || 0,
      responseData?.downPayment ?? 0,
      responseData?.rebate ?? 0,
      netTrade,
    ]) || 0
  );
}

/**
 * Calculates the sum of values given a list of fields in ResponseData.
 * @param responseData - responseData
 * @param sumValue - SumValueType which holds calculation fields
 */
export function calculateSumValue(
  responseData: FinanceResponseData,
  sumValue: SumValueType,
): number {
  let total = 0;

  if (sumValue.responseDataFields) {
    total += sumValue.responseDataFields.reduce((a, responseDataField) => {
      const val = get(responseData, responseDataField);
      return a + (get(val, 'value') || get(val, 'amount') || val || 0);
    }, 0);
  }

  if (sumValue.otherCharges) {
    total += sumValue.otherCharges.reduce((a, otherChargeField) => {
      const val = get(responseData, otherChargeField);
      return a + (sumBy(val, 'amount') || 0);
    }, 0);
  }

  if (sumValue.feeTypes) {
    const fees = responseData.finalizedFeeList?.filter(f =>
      sumValue.feeTypes?.includes(f.type),
    );
    total += sumBy(fees, 'value') || 0;
  }

  if (sumValue.productCategories) {
    const products = responseData.finalizedProductList?.filter(p =>
      sumValue.productCategories?.includes(p.category ?? undefined),
    );
    total += sumBy(products, 'value') || 0;
  }

  if (sumValue.insuranceTypes) {
    total += sumValue.insuranceTypes.reduce((a, insuranceType) => {
      const insurances = responseData?.insurances?.filter(
        i => i.type === insuranceType,
      );
      return a + (sumBy(insurances, 'premium') || 0);
    }, 0);
  }

  if (
    sumValue.downPaymentPayoff ||
    sumValue.downPaymentWithMinZero ||
    sumValue.subtractDownPaymentWithMinZero
  ) {
    const totalDownpayment = calculateTotalDownPayment(responseData);

    if (sumValue.downPaymentPayoff) {
      total += Math.max(-totalDownpayment, 0);
    }
    if (sumValue.subtractDownPaymentWithMinZero) {
      total -= Math.max(totalDownpayment, 0);
    }
    if (sumValue.downPaymentWithMinZero) {
      total += Math.max(totalDownpayment, 0);
    }
  }

  return total;
}
